import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { fetchWithAuth } from '../lib/api';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SyncIcon from '@mui/icons-material/Sync';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MyDataSetupWizard from './MyDataSetupWizard';
import { useBilling } from '../hooks/useBilling';
import { useNavigate } from 'react-router-dom';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#008060' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008060' },
  },
};

const MyDataSettings = () => {
  const navigate = useNavigate();
  const { hasFeature } = useBilling();
  const myDataEnabled = hasFeature('myDataSync');
  const [mydataSettings, setMydataSettings] = useState({
    mydata_user_id: '',
    mydata_subscription_key: '',
    mydata_company_vat: '',
    mydata_sync_from_date: dayjs('2025-11-01'),
    mydata_last_sync: '',
    taxisnet_username: '',
    taxisnet_password: '',
    gemi_api_key: '',
  });

  const [autoSync, setAutoSync] = useState({
    enabled: false, time: '09:00', lastRun: null, lastResult: null, lastReconciliation: null,
  });
  const [autoSyncLogs, setAutoSyncLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [autoSyncLoading, setAutoSyncLoading] = useState(false);
  const [runningManualSync, setRunningManualSync] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showWizard, setShowWizard] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    loadMydataSettings();
    loadAutoSyncStatus();
  }, []);

  const loadMydataSettings = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth('/api/settings').then((r) => r.json());
      const userIdRes = await fetchWithAuth('/api/settings/mydata_user_id').then((r) => r.json());
      const keyRes = await fetchWithAuth('/api/settings/mydata_subscription_key').then((r) => r.json());
      const vatRes = await fetchWithAuth('/api/settings/mydata_company_vat').then((r) => r.json());
      const taxisUserRes = await fetchWithAuth('/api/settings/taxisnet_username').then((r) => r.json());
      const taxisPassRes = await fetchWithAuth('/api/settings/taxisnet_password').then((r) => r.json());
      const gemiKeyRes = await fetchWithAuth('/api/settings/gemi_api_key').then((r) => r.json());

      let syncFromDate = '2025-11-01';
      if (response.mydata_sync_from_date && response.mydata_sync_from_date !== '***encrypted***' && typeof response.mydata_sync_from_date === 'string') {
        syncFromDate = response.mydata_sync_from_date;
      }
      try {
        const dateRes = await fetchWithAuth('/api/settings/mydata_sync_from_date').then((r) => r.json());
        if (dateRes?.value && dateRes.value !== '***encrypted***') syncFromDate = dateRes.value;
      } catch {}
      if (!dayjs(syncFromDate).isValid()) syncFromDate = '2025-11-01';

      const loadedUserId = userIdRes.value || '';
      const loadedKey = keyRes.value || '';
      setMydataSettings({
        mydata_user_id: loadedUserId,
        mydata_subscription_key: loadedKey,
        mydata_company_vat: vatRes.value || '',
        mydata_sync_from_date: dayjs(syncFromDate).isValid() ? dayjs(syncFromDate) : dayjs('2025-11-01'),
        mydata_last_sync: response.mydata_last_sync || '',
        taxisnet_username: taxisUserRes.value || '',
        taxisnet_password: taxisPassRes.value || '',
        gemi_api_key: gemiKeyRes.value || '',
      });
      setIsConfigured(!!(loadedUserId && loadedKey));
    } catch (error) {
      console.error('Error loading myDATA settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAutoSyncStatus = async () => {
    try {
      const response = await fetchWithAuth('/api/mydata/auto-sync/status').then((r) => r.json());
      setAutoSync({
        enabled: response.enabled || false,
        time: response.time || '09:00',
        lastRun: response.lastRun,
        lastResult: response.lastResult,
        lastReconciliation: response.lastReconciliation || response.lastResult?.reconciliation || null,
      });
    } catch {}
  };

  const formatReportDate = (dateStr) => {
    if (!dateStr) return '';
    const d = dayjs(dateStr);
    return d.isValid() ? d.format('DD/MM/YYYY') : dateStr;
  };

  const buildSyncSummaryMessage = (reconciliation, imported, skipped) => {
    if (!reconciliation) {
      return `Συγχρονισμός ολοκληρώθηκε: ${imported} εισήχθησαν, ${skipped} παραλείφθηκαν`;
    }
    const period = `${formatReportDate(reconciliation.dateFrom)} – ${formatReportDate(reconciliation.dateTo)}`;
    const mode = reconciliation.incremental ? 'ενημερωτικό' : 'πλήρες';
    return `Εισήχθησαν ${imported} νέα (${period}, ${mode}). ${skipped} ήδη υπήρχαν.`;
  };

  const loadAutoSyncLogs = async () => {
    try {
      const response = await fetchWithAuth('/api/mydata/auto-sync/logs?limit=10').then((r) => r.json());
      setAutoSyncLogs(response);
    } catch {}
  };

  const handleAutoSyncToggle = async (event) => {
    const enabled = event.target.checked;
    setAutoSyncLoading(true);
    try {
      if (enabled) {
        await fetchWithAuth('/api/mydata/auto-sync/enable', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ time: autoSync.time }) });
        setSnackbar({ open: true, message: `Ο αυτόματος συγχρονισμός ενεργοποιήθηκε στις ${autoSync.time}`, severity: 'success' });
      } else {
        await fetchWithAuth('/api/mydata/auto-sync/disable', { method: 'POST' });
        setSnackbar({ open: true, message: 'Ο αυτόματος συγχρονισμός απενεργοποιήθηκε', severity: 'info' });
      }
      setAutoSync({ ...autoSync, enabled });
    } catch {
      setSnackbar({ open: true, message: 'Σφάλμα αλλαγής ρύθμισης', severity: 'error' });
    } finally {
      setAutoSyncLoading(false);
    }
  };

  const handleAutoSyncTimeChange = async (newTime) => {
    if (!newTime || !newTime.isValid()) return;
    const timeString = newTime.format('HH:mm');
    setAutoSync({ ...autoSync, time: timeString });
    if (autoSync.enabled) {
      try {
        await fetchWithAuth('/api/mydata/auto-sync/time', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ time: timeString }) });
        setSnackbar({ open: true, message: `Η ώρα συγχρονισμού ενημερώθηκε στις ${timeString}`, severity: 'success' });
      } catch {
        setSnackbar({ open: true, message: 'Σφάλμα ενημέρωσης ώρας', severity: 'error' });
      }
    }
  };

  const handleRunNow = async () => {
    setRunningManualSync(true);
    try {
      const response = await fetchWithAuth('/api/mydata/auto-sync/run-now', { method: 'POST' }).then((r) => r.json());
      setSnackbar({
        open: true,
        message: buildSyncSummaryMessage(response.reconciliation, response.imported || 0, response.skipped || 0),
        severity: 'success',
      });
      await loadAutoSyncStatus();
      await loadMydataSettings();
      if (showLogs) await loadAutoSyncLogs();
    } catch {
      setSnackbar({ open: true, message: 'Σφάλμα συγχρονισμού', severity: 'error' });
    } finally {
      setRunningManualSync(false);
    }
  };

  const handleToggleLogs = () => {
    if (!showLogs && autoSyncLogs.length === 0) loadAutoSyncLogs();
    setShowLogs(!showLogs);
  };

  const handleMydataChange = (field) => (event) => {
    setMydataSettings({ ...mydataSettings, [field]: event.target.value });
  };

  const handleDateChange = (newDate) => {
    setMydataSettings({ ...mydataSettings, mydata_sync_from_date: newDate });
  };

  const handleSaveMydata = async () => {
    try {
      setSaving(true);
      await fetchWithAuth('/api/settings/mydata_user_id', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: mydataSettings.mydata_user_id }) });
      await fetchWithAuth('/api/settings/mydata_subscription_key', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: mydataSettings.mydata_subscription_key }) });
      await fetchWithAuth('/api/settings/mydata_company_vat', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: mydataSettings.mydata_company_vat }) });

      if (!mydataSettings.mydata_sync_from_date) throw new Error('Η ημερομηνία είναι υποχρεωτική');
      let dateString;
      if (typeof mydataSettings.mydata_sync_from_date.format === 'function') {
        if (!mydataSettings.mydata_sync_from_date.isValid()) throw new Error('Μη έγκυρη ημερομηνία');
        dateString = mydataSettings.mydata_sync_from_date.format('YYYY-MM-DD');
      } else if (typeof mydataSettings.mydata_sync_from_date === 'string') {
        dateString = mydataSettings.mydata_sync_from_date;
        const testDate = dayjs(dateString);
        if (!testDate.isValid()) throw new Error('Μη έγκυρη μορφή ημερομηνίας');
        dateString = testDate.format('YYYY-MM-DD');
      } else {
        throw new Error('Μη έγκυρη ημερομηνία');
      }
      await fetchWithAuth('/api/settings/mydata_sync_from_date', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: dateString }) });

      await fetchWithAuth('/api/settings/taxisnet_username', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: mydataSettings.taxisnet_username }) });
      await fetchWithAuth('/api/settings/taxisnet_password', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: mydataSettings.taxisnet_password }) });
      await fetchWithAuth('/api/settings/gemi_api_key', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: mydataSettings.gemi_api_key }) });

      setSnackbar({ open: true, message: 'Οι ρυθμίσεις myDATA αποθηκεύτηκαν επιτυχώς', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Σφάλμα αποθήκευσης ρυθμίσεων', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleWizardComplete = () => {
    setShowWizard(false);
    loadMydataSettings();
    loadAutoSyncStatus();
    setSnackbar({ open: true, message: 'Η σύνδεση με το myDATA ολοκληρώθηκε επιτυχώς!', severity: 'success' });
  };

  const SectionHeader = ({ icon, iconBg, title, subtitle }) => (
    <Box sx={{
      p: { xs: 2, md: 2.5 },
      borderBottom: '1px solid rgba(0,0,0,0.06)',
      bgcolor: 'rgba(0,0,0,0.01)',
      display: 'flex', alignItems: 'center', gap: 1.5,
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1.5,
        background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { sx: { color: '#fff', fontSize: 18 } })}
      </Box>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1, md: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              background: 'linear-gradient(135deg, #008060 0%, #004c3f 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(0, 128, 96, 0.3)',
            }}>
              <SyncIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                myDATA Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Σύνδεση με ΑΑΔΕ για αυτόματη εισαγωγή τιμολογίων
              </Typography>
            </Box>
          </Box>
        </Box>

        {!myDataEnabled && (
          <Alert
            severity="info"
            sx={{ mb: 3, borderRadius: 2.5 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/settings/billing')} sx={{ fontWeight: 600 }}>
                Αναβάθμιση
              </Button>
            }
          >
            Το myDATA sync είναι διαθέσιμο στα πλάνα Pro και Business.
          </Alert>
        )}

        {!myDataEnabled && (
          <Alert
            severity="info"
            sx={{ mb: 3, borderRadius: 2.5 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/settings/billing')} sx={{ fontWeight: 600 }}>
                Αναβάθμιση
              </Button>
            }
          >
            Το myDATA sync είναι διαθέσιμο στα πλάνα Pro και Business.
          </Alert>
        )}

        {/* Setup Wizard Banner */}
        {!loading && !isConfigured && !showWizard && myDataEnabled && (
          <Paper sx={{
            mb: 3, borderRadius: 3, overflow: 'hidden',
            border: '2px solid rgba(0,128,96,0.3)',
            background: 'linear-gradient(135deg, rgba(0,128,96,0.04) 0%, rgba(0,128,96,0.01) 100%)',
          }}>
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: 2,
                background: 'linear-gradient(135deg, #008060, #004c3f)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(0,128,96,0.3)',
              }}>
                <RocketLaunchIcon sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Δεν έχετε συνδέσει ακόμα το myDATA
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Χρησιμοποιήστε τον Οδηγό Σύνδεσης για εύκολη ρύθμιση σε 5 λεπτά.
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                onClick={() => setShowWizard(true)}
                startIcon={<RocketLaunchIcon />}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4,
                  boxShadow: '0 4px 14px rgba(0,128,96,0.3)',
                }}
              >
                Οδηγός Σύνδεσης
              </Button>
            </Box>
          </Paper>
        )}

        {/* Wizard Section */}
        {showWizard && (
          <Box sx={{ mb: 4 }}>
            <MyDataSetupWizard onComplete={handleWizardComplete} initialVat={mydataSettings.mydata_company_vat} />
            <Button variant="text" size="small" onClick={() => setShowWizard(false)} sx={{ mt: 1, textTransform: 'none' }}>
              Κλείσιμο οδηγού — ρύθμιση χειροκίνητα
            </Button>
          </Box>
        )}

        {/* Connection Status */}
        {!loading && isConfigured && (
          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setShowWizard(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Επαναρύθμιση
              </Button>
            }
          >
            Το myDATA είναι συνδεδεμένο. Τα τιμολόγια εισάγονται αυτόματα.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#008060' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* myDATA Credentials Section */}
            <Paper sx={{
              borderRadius: 3, overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.06)',
            }}>
              <SectionHeader
                icon={<VpnKeyIcon />}
                iconBg="linear-gradient(135deg, #008060 0%, #004c3f 100%)"
                title="Στοιχεία myDATA"
                subtitle="Κλειδιά πρόσβασης ΑΑΔΕ"
              />
              <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  fullWidth
                  label="User ID"
                  value={mydataSettings.mydata_user_id}
                  onChange={handleMydataChange('mydata_user_id')}
                  placeholder="Το User ID σας από το myDATA (π.χ. 123456789)"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                  }}
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Subscription Key"
                  type="password"
                  value={mydataSettings.mydata_subscription_key}
                  onChange={handleMydataChange('mydata_subscription_key')}
                  placeholder="Το Subscription Key σας"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><VpnKeyIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                  }}
                  sx={inputSx}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="ΑΦΜ Εταιρείας"
                      value={mydataSettings.mydata_company_vat}
                      onChange={handleMydataChange('mydata_company_vat')}
                      placeholder="123456789"
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Ημερομηνία Έναρξης Συγχρονισμού"
                      value={mydataSettings.mydata_sync_from_date}
                      onChange={handleDateChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: inputSx,
                          InputProps: {
                            startAdornment: <InputAdornment position="start"><CalendarMonthIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                {mydataSettings.mydata_last_sync && (
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0,128,96,0.04)', border: '1px solid rgba(0,128,96,0.1)' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#008060' }} />
                      Τελευταίος συγχρονισμός: <strong>{new Date(mydataSettings.mydata_last_sync).toLocaleString('el-GR')}</strong>
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* TAXISnet Section */}
            <Paper sx={{
              borderRadius: 3, overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.06)',
            }}>
              <SectionHeader
                icon={<LockIcon />}
                iconBg="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                title="TAXISnet Credentials"
                subtitle="Αναζήτηση ονομάτων προμηθευτών"
              />
              <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="TAXISnet Username"
                      value={mydataSettings.taxisnet_username}
                      onChange={handleMydataChange('taxisnet_username')}
                      placeholder="Το username TAXISnet"
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="TAXISnet Password"
                      type="password"
                      value={mydataSettings.taxisnet_password}
                      onChange={handleMydataChange('taxisnet_password')}
                      placeholder="Το password TAXISnet"
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                      }}
                      sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* GEMI Section */}
            <Paper sx={{
              borderRadius: 3, overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.06)',
            }}>
              <SectionHeader
                icon={<VpnKeyIcon />}
                iconBg="linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)"
                title="ΓΕΜΗ API Key"
                subtitle="Προτιμάται έναντι TAXISnet"
              />
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <TextField
                  fullWidth
                  label="ΓΕΜΗ API Key"
                  type="password"
                  value={mydataSettings.gemi_api_key}
                  onChange={handleMydataChange('gemi_api_key')}
                  placeholder="Το API Key από το ΓΕΜΗ Open Data API"
                  helperText="Το ΓΕΜΗ API είναι REST JSON και προτιμάται έναντι του TAXISnet SOAP API"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><VpnKeyIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
                  }}
                  sx={inputSx}
                />
              </Box>
            </Paper>

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSaveMydata}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4, py: 1.2,
                  boxShadow: '0 4px 14px rgba(0,128,96,0.3)',
                  fontSize: '0.95rem',
                }}
              >
                {saving ? 'Αποθήκευση...' : 'Αποθήκευση Ρυθμίσεων'}
              </Button>
            </Box>

            {/* Auto-Sync Section */}
            <Paper sx={{
              borderRadius: 3, overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: autoSync.enabled ? '1px solid rgba(0,128,96,0.2)' : '1px solid rgba(0,0,0,0.06)',
              transition: 'border-color 0.3s',
            }}>
              <Box sx={{
                p: { xs: 2, md: 2.5 },
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                bgcolor: autoSync.enabled ? 'rgba(0,128,96,0.03)' : 'rgba(0,0,0,0.01)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                transition: 'background-color 0.3s',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.5,
                    background: autoSync.enabled
                      ? 'linear-gradient(135deg, #008060 0%, #004c3f 100%)'
                      : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.3s',
                  }}>
                    <ScheduleIcon sx={{ color: '#fff', fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
                      Αυτόματος Συγχρονισμός
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Καθημερινή εισαγωγή τιμολογίων</Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoSync.enabled}
                      onChange={handleAutoSyncToggle}
                      disabled={autoSyncLoading}
                      color="primary"
                    />
                  }
                  label={
                    <Chip
                      label={autoSync.enabled ? 'Ενεργό' : 'Ανενεργό'}
                      size="small"
                      sx={{
                        fontWeight: 600, fontSize: '0.7rem',
                        ...(autoSync.enabled
                          ? { bgcolor: '#e8f5e9', color: '#2e7d32' }
                          : { bgcolor: 'rgba(0,0,0,0.06)', color: '#666' }),
                      }}
                    />
                  }
                  labelPlacement="start"
                  sx={{ mr: 0 }}
                />
              </Box>

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Όταν είναι ενεργοποιημένος, ο συγχρονισμός εκτελείται αυτόματα μία φορά την ημέρα στην καθορισμένη ώρα.
                  Κάθε run σκανάρει τις τελευταίες ~14 ημέρες (overlap) για να καλύπτονται καθυστερήσεις AADE.
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                  <TimePicker
                    label="Ώρα συγχρονισμού"
                    value={dayjs(`2000-01-01T${autoSync.time}`)}
                    onChange={handleAutoSyncTimeChange}
                    ampm={false}
                    slotProps={{ textField: { fullWidth: true, sx: inputSx } }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={runningManualSync ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                    onClick={handleRunNow}
                    disabled={runningManualSync}
                    fullWidth
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, minHeight: 56 }}
                  >
                    {runningManualSync ? 'Συγχρονισμός...' : 'Εκτέλεση Τώρα'}
                  </Button>
                  <Button
                    variant="text"
                    startIcon={showLogs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    endIcon={<HistoryIcon />}
                    onClick={handleToggleLogs}
                    fullWidth
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, minHeight: 56 }}
                  >
                    Ιστορικό
                  </Button>
                </Box>

                {/* Last sync info + reconciliation */}
                {autoSync.lastRun && (
                  <Box sx={{
                    p: 2, borderRadius: 2, mb: 2,
                    bgcolor: autoSync.lastResult?.success !== false ? 'rgba(0,128,96,0.04)' : 'rgba(239,68,68,0.04)',
                    border: `1px solid ${autoSync.lastResult?.success !== false ? 'rgba(0,128,96,0.15)' : 'rgba(239,68,68,0.15)'}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {autoSync.lastResult?.success !== false ? (
                        <CheckCircleIcon sx={{ color: '#008060', fontSize: 18 }} />
                      ) : (
                        <ErrorIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Τελευταίος: {new Date(autoSync.lastRun).toLocaleString('el-GR')}
                      </Typography>
                    </Box>
                    {autoSync.lastReconciliation && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Περίοδος: {formatReportDate(autoSync.lastReconciliation.dateFrom)} – {formatReportDate(autoSync.lastReconciliation.dateTo)}
                        {autoSync.lastReconciliation.incremental ? ' (ενημερωτικό)' : ' (πλήρες)'}
                      </Typography>
                    )}
                    {autoSync.lastResult && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: autoSync.lastReconciliation?.newImports?.length ? 1.5 : 0 }}>
                        {autoSync.lastReconciliation?.apiTotal != null && (
                          <Chip size="small" label={`API: ${autoSync.lastReconciliation.apiTotal}`}
                            sx={{ bgcolor: 'rgba(0,128,96,0.08)', color: '#006e52', fontWeight: 600, fontSize: '0.7rem' }} />
                        )}
                        <Chip size="small" label={`Εισήχθησαν: ${autoSync.lastResult.imported || 0}`}
                          sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }} />
                        <Chip size="small" label={`Παραλείφθηκαν: ${autoSync.lastResult.skipped || 0}`}
                          sx={{ bgcolor: 'rgba(0,0,0,0.06)', color: '#666', fontWeight: 600, fontSize: '0.7rem' }} />
                        {(autoSync.lastResult.errors > 0 || autoSync.lastReconciliation?.errorCount > 0) && (
                          <Chip size="small" label={`Σφάλματα: ${autoSync.lastReconciliation?.errorCount ?? autoSync.lastResult.errors}`}
                            sx={{ bgcolor: 'rgba(239,68,68,0.1)', color: '#dc2626', fontWeight: 600, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}
                    {autoSync.lastReconciliation?.newImports?.length > 0 && (
                      <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.06)', mt: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Ημερομηνία</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Προμηθευτής</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Ποσό</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {autoSync.lastReconciliation.newImports.map((row) => (
                              <TableRow key={row.mark}>
                                <TableCell sx={{ fontSize: '0.75rem' }}>{formatReportDate(row.date)}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {row.supplier || row.vat || '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                  {typeof row.amount === 'number' ? row.amount.toLocaleString('el-GR', { style: 'currency', currency: 'EUR' }) : row.amount}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}

                {/* Logs */}
                <Collapse in={showLogs}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#555', mb: 1.5, display: 'block' }}>
                      Ιστορικό Αυτόματων Συγχρονισμών
                    </Typography>
                    {autoSyncLogs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Δεν υπάρχουν εγγραφές ακόμα.
                      </Typography>
                    ) : (
                      <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.06)' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                              <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ημερομηνία</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Κατάσταση</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Εισαγωγές</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Παράλειψη</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Διάρκεια</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {autoSyncLogs.map((log) => (
                              <TableRow key={log.id} sx={{ '&:hover': { bgcolor: 'rgba(0,128,96,0.02)' } }}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {new Date(log.sync_date).toLocaleString('el-GR')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={log.success ? 'Επιτυχία' : 'Αποτυχία'}
                                    sx={{
                                      fontWeight: 600, fontSize: '0.7rem',
                                      ...(log.success
                                        ? { bgcolor: '#e8f5e9', color: '#2e7d32' }
                                        : { bgcolor: 'rgba(239,68,68,0.1)', color: '#dc2626' }),
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{log.imported_count || 0}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">{log.skipped_count || 0}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">
                                    {log.duration_seconds ? `${log.duration_seconds.toFixed(1)}s` : '-'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Collapse>
              </Box>
            </Paper>
          </Box>
        )}

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default MyDataSettings;
